import { apiClient } from '@/shared/api';
import { API_ENDPOINTS } from '@/config';
import {
    SetupPasswordPayload,
    ActivateCardPayload,
    OnboardingSuccessResponse,
    CustomerIdValidatePayload,
    CustomerIdValidateResponse,
    UserSelfOnboardPayload,
    UserSelfOnboardResponse,
} from '../types/onboarding.types';
import { buildCommonHeaders } from '@/shared/http/commonHeaders';
import {
    trackAcceptInviteCalled,
    trackAcceptInviteResult,
    trackSetupPasswordCalled,
    trackSetupPasswordResult,
} from '@/shared/analytics/onboarding';

const emptySession = {};

export const onboardingService = {
    /**
     * Read-only validation of the invitation token on page load.
     *
     * NO mintea `password_token` ni cambia estados en Mongo: solo confirma que
     * el token es válido y devuelve datos para pintar la pantalla (email,
     * status, invitation_valid). Es idempotente, por eso es seguro llamarlo en
     * cada carga/onMount. Si el token está quemado/expirado o el usuario no está
     * `invited`/`in_progress`, el backend responde con error HTTP (400/404/409).
     */
    validateInvite: async (token: string): Promise<OnboardingSuccessResponse> => {
        const newHeaders = buildCommonHeaders({
            withLocation: true
        });

        const response = await apiClient.post<OnboardingSuccessResponse>(
            API_ENDPOINTS.onboarding.validateInvite,
            { data: { token }, session: emptySession },
            { headers: newHeaders }
        );

        return response.data.data;
    },

    /**
     * Accepts the invitation and mints a fresh single-use `password_token`.
     *
     * Debe llamarse JUSTO antes de confirmar el password (no en la carga): cada
     * accept mintea+invalida el token, así que el `password_token` devuelto se
     * usa inmediatamente en `setupPassword` y nunca se cachea/persiste.
     */
    acceptInvite: async (token: string): Promise<OnboardingSuccessResponse> => {
        const newHeaders = buildCommonHeaders({
            withLocation: true
        });

        trackAcceptInviteCalled();
        try {
            const response = await apiClient.post<OnboardingSuccessResponse>(
                API_ENDPOINTS.onboarding.acceptInvite,
                { data: { token, is_human: true }, session: emptySession },
                { headers: newHeaders }
            );

            const result = response.data.data;
            trackAcceptInviteResult(true, result);
            return result;
        } catch (err) {
            trackAcceptInviteResult(false, undefined, err);
            throw err;
        }
    },

    /**
     * Sets the password and logs the user in (returns session context theoretically)
     */
    setupPassword: async (payload: SetupPasswordPayload): Promise<OnboardingSuccessResponse> => {
        trackSetupPasswordCalled(payload.token);
        try {
            const response = await apiClient.post<OnboardingSuccessResponse>(
                API_ENDPOINTS.onboarding.setupPassword,
                { data: { data: payload } }
            );
            trackSetupPasswordResult(true, payload.token);
            return response;
        } catch (err) {
            trackSetupPasswordResult(false, payload.token, err);
            throw err;
        }
    },

    /**
     * Activates the physical card with PAN segment and exact NIP
     */
    activateCard: async (payload: ActivateCardPayload): Promise<OnboardingSuccessResponse> => {
        const newHeaders = buildCommonHeaders({
            withLocation: true
        });

        return apiClient.post<OnboardingSuccessResponse>(
            API_ENDPOINTS.onboarding.activateCard,
            { data: payload, session: emptySession },
            { headers: newHeaders }
        );
    },

    /**
     * Validates the company identifier (customer_id) entered during user signup
     *
     * Public/no-login command: the BFF unwraps `data` once before forwarding, and
     * for non-authenticated context it does not re-wrap, so we double-wrap here
     * (same pattern as forgotPassword / resetPassword / setupPassword) so the
     * upstream service receives `{ data: { customer_id } }`.
     */
    validateCustomerId: async (payload: CustomerIdValidatePayload): Promise<CustomerIdValidateResponse> => {
        return apiClient.post<CustomerIdValidateResponse>(
            API_ENDPOINTS.onboarding.customerIdValidate,
            { data: { data: payload } }
        );
    },

    /**
     * Creates the user during self-service onboarding.
     * Public/no-login command — double-wrapped like the others.
     */
    selfOnboard: async (payload: UserSelfOnboardPayload): Promise<UserSelfOnboardResponse> => {
        return apiClient.post<UserSelfOnboardResponse>(
            API_ENDPOINTS.onboarding.userSelfOnboard,
            { data: { data: payload } }
        );
    },

    /**
     * Gets the account for the user
     */
    accountGet: async (user_uuid: string): Promise<OnboardingSuccessResponse> => {
        const newHeaders = buildCommonHeaders({
            withLocation: true
        });

        return apiClient.post<OnboardingSuccessResponse>(
            API_ENDPOINTS.onboarding.accountGet,
            { data: {}, session: emptySession },
            { headers: newHeaders, params: { user_uuid } }
        );
    }
};
